import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, auth } from "@/lib/api";
import Navbar from "@/components/Navbar";

export default function Profile() {
  const [me, setMe] = useState(auth.user());
  const [loading, setLoading] = useState(true);
  const [showBoost, setShowBoost] = useState(false);
  const [paying, setPaying] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setMe(data);
      localStorage.setItem("fm_user", JSON.stringify(data));
    } catch (e) { toast.error("Failed to load profile"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Stripe redirect for boost
  useEffect(() => {
    const sid = params.get("session_id");
    const boost = params.get("boost");
    if (sid && boost) {
      pollBoost(sid);
    } else if (params.get("cancelled")) {
      toast.error("Payment cancelled");
      params.delete("cancelled"); setParams(params);
    }
    // eslint-disable-next-line
  }, []);

  const pollBoost = async (sid, attempts = 0) => {
    if (attempts >= 8) return;
    try {
      const { data } = await api.get(`/payments/stripe/status/${sid}`);
      if (data.payment_status === "paid") {
        toast.success("🚀 Boost activated — 100 impressions!");
        params.delete("session_id"); params.delete("boost"); setParams(params);
        await load();
        return;
      }
      setTimeout(() => pollBoost(sid, attempts + 1), 2000);
    } catch (e) {}
  };

  const startBoostStripe = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/stripe/boost-checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error("Stripe error"); setPaying(false); }
  };

  const startBoostPaypal = async () => {
    setPaying(true);
    try {
      const { data: order } = await api.post("/payments/paypal/boost-order");
      const popup = window.open(`https://www.sandbox.paypal.com/checkoutnow?token=${order.order_id}`, "paypal", "width=500,height=700");
      const timer = setInterval(async () => {
        if (popup?.closed) {
          clearInterval(timer);
          try {
            const { data: cap } = await api.post("/payments/paypal/capture", { order_id: order.order_id });
            if (cap.payment_status === "paid") { toast.success("🚀 Boost activated!"); setShowBoost(false); await load(); }
            else toast.error("Payment not completed");
          } catch (e) { toast.error(e?.response?.data?.detail || "PayPal capture failed"); }
          finally { setPaying(false); }
        }
      }, 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PayPal error");
      setPaying(false);
    }
  };

  const refreshLocation = () => {
    if (!navigator.geolocation) return toast.error("Geo unavailable");
    navigator.geolocation.getCurrentPosition(async (p) => {
      try {
        await api.post("/auth/location", { lat: p.coords.latitude, lng: p.coords.longitude, city: me?.city });
        toast.success("Location updated");
        setMe(m => ({ ...m, lat: p.coords.latitude, lng: p.coords.longitude }));
      } catch (e) { toast.error("Update failed"); }
    });
  };

  if (loading || !me) return <div><Navbar /><div className="p-10 font-mono text-sm">LOADING...</div></div>;

  const boostRemaining = me.boost_remaining || 0;

  return (
    <div>
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="font-mono text-xs uppercase tracking-widest text-[#555]">[ PROFILE / STATS ]</div>
        <h1 className="font-display text-5xl tracking-tighter mt-1 mb-8">{me.name?.split(" ")[0]?.toUpperCase()}.</h1>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5">
            <div className="brutal bg-white">
              <img src={me.photo_base64} alt="me" className="w-full aspect-square object-cover" />
              <div className="p-5 border-t border-black">
                <div className="font-display text-2xl">{me.name}, {me.age}</div>
                <div className="font-mono text-xs text-[#555] mt-1">{me.city || "—"}</div>
                {me.bio && <p className="mt-3 text-sm">{me.bio}</p>}
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-4">
            <StatCard label="AI RATING" value={me.ai_rating?.toFixed(1) ?? "—"} suffix="/10" highlight testid="stat-ai" />
            <StatCard
              label="COMMUNITY RATING"
              value={me.community_rating?.toFixed(2) ?? "—"}
              suffix={`/10 · ${me.community_votes ?? 0} VOTES`}
              testid="stat-community"
            />

            {/* Boost card */}
            <div className="brutal bg-white p-6" data-testid="boost-card">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-mono text-[10px] uppercase text-[#E53935]">🚀 BOOST</div>
                  <div className="font-display text-2xl mt-1">
                    {boostRemaining > 0 ? `${boostRemaining} impressions left` : "Get to the top of the deck"}
                  </div>
                  <div className="font-mono text-xs text-[#555] mt-1">
                    ${me.boost_price ?? 4.99} for {me.boost_impressions ?? 100} pinned impressions — regardless of AI score.
                  </div>
                </div>
                <button
                  data-testid="open-boost"
                  onClick={() => setShowBoost(true)}
                  className="px-5 py-3 bg-[#E53935] hover:bg-[#C62828] text-white font-mono text-xs whitespace-nowrap"
                >
                  {boostRemaining > 0 ? "ADD MORE" : "BOOST ME"} →
                </button>
              </div>
            </div>

            <div className="brutal bg-white p-6">
              <div className="font-mono text-xs uppercase mb-4">LOCATION</div>
              <div className="font-mono text-sm">
                {me.lat != null && me.lng != null
                  ? `${me.lat.toFixed(3)}, ${me.lng.toFixed(3)}`
                  : "NOT SET"}
              </div>
              <button data-testid="profile-refresh-geo" onClick={refreshLocation} className="mt-4 px-4 py-2 border border-black font-mono text-xs hover:bg-black hover:text-white">
                REFRESH LOCATION
              </button>
            </div>
          </div>
        </div>
      </div>

      {showBoost && (
        <BoostModal
          price={me.boost_price ?? 4.99}
          impressions={me.boost_impressions ?? 100}
          paying={paying}
          onStripe={startBoostStripe}
          onPaypal={startBoostPaypal}
          onClose={() => setShowBoost(false)}
        />
      )}
    </div>
  );
}

function BoostModal({ price, impressions, paying, onStripe, onPaypal, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="brutal bg-white max-w-md w-full" data-testid="boost-modal">
        <div className="p-6 border-b border-black flex items-center justify-between bg-[#E53935] text-white">
          <div>
            <div className="font-mono text-[10px]">🚀 BOOST NOW</div>
            <div className="font-display text-2xl tracking-tighter">JUMP THE LINE</div>
          </div>
          <button onClick={onClose} data-testid="boost-close" className="font-mono text-sm hover:text-black">✕</button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <div className="font-display text-6xl tracking-tighter">${price}</div>
            <div className="font-mono text-[10px] text-[#555]">{impressions} PINNED IMPRESSIONS · ONE-TIME</div>
          </div>
          <ul className="space-y-2 font-mono text-xs">
            <li>✓ TOP OF DECK FOR THE NEXT {impressions} VIEWERS</li>
            <li>✓ ★ BOOSTED BADGE ON YOUR CARD</li>
            <li>✓ WORKS REGARDLESS OF AI SCORE</li>
            <li>✓ STACKS WITH EXISTING BOOST</li>
          </ul>
          <button
            data-testid="boost-stripe"
            onClick={onStripe}
            disabled={paying}
            className="w-full py-4 bg-black text-white font-mono hover:bg-[#E53935] disabled:opacity-60"
          >
            {paying ? "REDIRECTING..." : `PAY $${price} WITH CARD`}
          </button>
          <button
            data-testid="boost-paypal"
            onClick={onPaypal}
            disabled={paying}
            className="w-full py-4 border-2 border-black font-mono hover:bg-black hover:text-white disabled:opacity-60"
          >
            {paying ? "OPENING PAYPAL..." : "PAY WITH PAYPAL"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, suffix, highlight, testid }) {
  return (
    <div className={`brutal ${highlight ? "bg-[#E53935] text-white" : "bg-white"} p-6 flex items-end justify-between`} data-testid={testid}>
      <div className="font-mono text-xs uppercase">{label}</div>
      <div className="text-right">
        <span className="font-display text-5xl tracking-tighter">{value}</span>
        <span className={`font-mono text-xs ml-2 ${highlight ? "text-white/80" : "text-[#555]"}`}>{suffix}</span>
      </div>
    </div>
  );
}
